import { useAddBuyersCertificateMutation } from '@api/buyersCert.api';
import { useLazyGetDealerDetailsQuery } from '@api/customer.api';
import { BuyersCertificate, Dealer, WarrantyPurchase } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import {
  Button,
  BuyersCertificateDatePicker,
  BuyersCertificateProductSelect,
  BuyersCertificateRadioGroup,
  BuyersCertificateSerialNumber,
  CustomTextField,
  getPublicAddress,
  Title,
  useBuyersCertificateCreateContext,
} from '@components';
import {
  useAppSelector,
  useAuth,
  useInternalRedirect,
  useSendSms,
  useSerialNumber,
  useToast,
} from '@hooks';
import { getSpecialPage } from '@store/app/app.slice';

import { Box, Checkbox, Divider, FormControlLabel, FormGroup, Grid, Switch } from '@mui/material';

import { getDealer } from '@store/dealer/dealer.slice';
import { colors } from '@themes/colors';
import { t } from '@translations';

import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';

type BuyersCertificateCreateFormProps = {
  textMessage?: string;
};

export const BuyersCertificateCreateForm = ({ textMessage }: BuyersCertificateCreateFormProps) => {
  const [requestAdd, { isLoading: isRequestAddLoading }] = useAddBuyersCertificateMutation();
  const { form } = useBuyersCertificateCreateContext();
  const { toast } = useToast();
  const { customerNo } = useAuth();
  const { sanitizeSerialNo } = useSerialNumber();
  const [fetchDealer, { isLoading: isFetchDealerLoading }] = useLazyGetDealerDetailsQuery();
  const dealer: Dealer = useSelector(getDealer);
  const dealerName = getPublicAddress(dealer)?.name;
  const [consent, setConsent] = useState(true);
  const redirect = useInternalRedirect();
  const { isBeremaUser } = useAuth();
  const dealerBuyersCertificateListPage = useAppSelector(
    getSpecialPage('dealerBuyersCertificateListPage')
  );
  const beremaBuyersCertificateListPage = useAppSelector(
    getSpecialPage('beremaBuyersCertificateListPage')
  );
  const buyersCertificateListPage = isBeremaUser
    ? beremaBuyersCertificateListPage
    : dealerBuyersCertificateListPage;
  const buyersCertificateListURL = buyersCertificateListPage || '/';

  const purchaseTypeOptions = [
    { label: t(TextCodes.BuyersCertificate.Block.Private), value: WarrantyPurchase.Private },
    { label: t(TextCodes.BuyersCertificate.Block.Company), value: WarrantyPurchase.Company },
  ];
  const { sendSms, createSmsMessage } = useSendSms();

  const handleConsentChange = () => {
    setConsent(!consent);
  };

  useEffect(() => {
    if (customerNo) {
      fetchDealer(customerNo).then((res) => {
        if (res.data) {
          form.setValue('dealerId', res?.data?.data?.id);
          form.setValue('dealerCustomerNo', res?.data?.data?.customerNo);
          form.setValue('dealerName', getPublicAddress(res?.data?.data)?.name);
        }
      });
    }
  }, [customerNo, fetchDealer, form]);

  const onSubmit = async (data: BuyersCertificate) => {
    try {
      if (data.productCustomModel) data.productModel = data.productCustomModel;
      if (data.serialNumber) data.serialNumber = sanitizeSerialNo(data.serialNumber);

      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (!Array.isArray(value)) {
          formData.append(key, value as string);
        }
      });
      const response = await requestAdd(formData).unwrap();

      const token = response?.data?.token;
      if (data.customerMobile && token && textMessage) {
        const message = createSmsMessage({ token, textMessage });
        sendSms({ to: data.customerMobile, message });
      }
      toast({
        severity: 'success',
        message: t(TextCodes.BuyersCertificate.Block.BuyersCertificateCreateSuccess),
      });
      form.reset();
      form.setValue('dealerId', dealer.id);
      form.setValue('dealerCustomerNo', dealer.customerNo);
      form.setValue('dealerName', dealerName);
      form.setValue('serialNumber', undefined);
      form.setValue('salesDate', undefined);
      form.setValue('productCustomModel', undefined);
      setConsent(true);
      redirect(buyersCertificateListURL);
    } catch (error) {
      console.error(error);
      toast({
        severity: 'error',
        message: (error as any)?.data?.data || 'Something went wrong',
      });
    }
  };

  return (
    <Box component='form'>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Title noDot variant='h3'>
            {t(TextCodes.Warranty.Block.Dealer)}
          </Title>
          <Grid
            container
            spacing={{
              xs: 0,
              md: 3,
            }}
          >
            <Grid item xs={12} md={8} lg={6}>
              <CustomTextField
                fullWidth
                label={t(TextCodes.Warranty.Block.DealerName)}
                variant='outlined'
                sx={{ mt: 5 }}
                {...form.register('dealerName')}
              />
              <BuyersCertificateRadioGroup
                name='purchaseType'
                label={t(TextCodes.Warranty.Block.PurchaseType)}
                options={purchaseTypeOptions}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} sm={6} lg={4}>
          <Title noDot variant='h3'>
            {t(TextCodes.Warranty.Block.Customer)}
          </Title>
          <CustomTextField
            fullWidth
            label={t(TextCodes.Warranty.Block.CustomerName)}
            variant='outlined'
            sx={{ mt: 5 }}
            {...form.register('customerName')}
          />
          <CustomTextField
            fullWidth
            label={t(TextCodes.BuyersCertificate.Block.Address)}
            variant='outlined'
            sx={{ mt: 5 }}
            {...form.register('customerAddress')}
          />

          <Grid
            container
            spacing={{
              xs: 0,
              md: 3,
            }}
          >
            <Grid item xs={12} md={4}>
              <CustomTextField
                fullWidth
                required
                label={t(TextCodes.BuyersCertificate.Block.PostCode)}
                variant='outlined'
                sx={{ mt: 5 }}
                {...form.register('customerPostCode', { required: true })}
                error={!!form.formState.errors.customerPostCode}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <CustomTextField
                fullWidth
                required
                label={t(TextCodes.BuyersCertificate.Block.City)}
                variant='outlined'
                sx={{ mt: 5 }}
                {...form.register('customerCity', { required: true })}
                error={!!form.formState.errors.customerCity}
              />
            </Grid>
          </Grid>
          <Grid
            container
            spacing={{
              xs: 0,
              md: 3,
            }}
          >
            <Grid item xs={12} md={6}>
              <CustomTextField
                fullWidth
                label={t(TextCodes.BuyersCertificate.Block.Email)}
                variant='outlined'
                sx={{ mt: 5 }}
                {...form.register('customerEmail')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomTextField
                fullWidth
                required={consent}
                label={t(TextCodes.BuyersCertificate.Block.Mobile)}
                variant='outlined'
                sx={{ mt: 5 }}
                {...form.register('customerMobile', { required: consent })}
                error={!!form.formState.errors.customerMobile}
              />
            </Grid>
          </Grid>
          <FormGroup
            sx={{
              mt: 2,
            }}
            onChange={handleConsentChange}
          >
            <FormControlLabel
              control={<Switch checked={consent} />}
              label={t(TextCodes.BuyersCertificate.Block.AskForConsent)}
            />
          </FormGroup>
        </Grid>
      </Grid>
      <Divider sx={{ my: 4 }} />
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Title noDot variant='h3'>
            {t(TextCodes.Warranty.Block.ProductInformation)}
          </Title>
          <Grid
            container
            spacing={{
              xs: 0,
              md: 3,
            }}
          >
            <BuyersCertificateProductSelect isEditEnabled />
          </Grid>
          <Grid
            container
            spacing={{
              xs: 0,
              md: 3,
            }}
          >
            <Grid item xs={12} md={6} lg={5}>
              <BuyersCertificateSerialNumber isEditEnabled />
            </Grid>
            <Grid item xs={12} md={6} lg={5}>
              <CustomTextField
                fullWidth
                label={t(TextCodes.Warranty.Block.EngineNo)}
                variant='outlined'
                sx={{ mt: 5 }}
                {...form.register('engineNumber')}
              />
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={5}>
              <BuyersCertificateDatePicker
                label={t(TextCodes.Warranty.Block.SaleDate)}
                name='salesDate'
                required
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={6} lg={5}>
          <Controller
            name='manualDelivered'
            control={form.control}
            defaultValue={false}
            render={({ field }) => (
              <FormGroup
                sx={{
                  display: 'inline',
                  color: !!form.formState.errors.manualDelivered ? colors.error.main : undefined,
                }}
              >
                <FormControlLabel
                  label={t(TextCodes.BuyersCertificate.Block.ManualDelivered)}
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      sx={{
                        color: !!form.formState.errors.manualDelivered
                          ? colors.error.main
                          : undefined,
                      }}
                    />
                  }
                />
              </FormGroup>
            )}
          />
          <Controller
            name='howItWorksInstructions'
            control={form.control}
            defaultValue={false}
            render={({ field }) => (
              <FormGroup
                sx={{
                  display: 'inline',
                  color: !!form.formState.errors.howItWorksInstructions
                    ? colors.error.main
                    : undefined,
                }}
              >
                <FormControlLabel
                  label={t(TextCodes.BuyersCertificate.Block.HowItWorksInstructions)}
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      sx={{
                        color: !!form.formState.errors.howItWorksInstructions
                          ? colors.error.main
                          : undefined,
                      }}
                    />
                  }
                />
              </FormGroup>
            )}
          />
          <Controller
            name='deliveredAndTested'
            control={form.control}
            defaultValue={false}
            render={({ field }) => (
              <FormGroup
                sx={{
                  display: 'inline',
                  color: !!form.formState.errors.deliveredAndTested ? colors.error.main : undefined,
                }}
              >
                <FormControlLabel
                  label={t(TextCodes.BuyersCertificate.Block.DeliveredAndTested)}
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      sx={{
                        color: !!form.formState.errors.deliveredAndTested
                          ? colors.error.main
                          : undefined,
                      }}
                    />
                  }
                />
              </FormGroup>
            )}
          />
        </Grid>
      </Grid>
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant='primary'
          type='submit'
          busy={isRequestAddLoading}
          disabled={isRequestAddLoading || isFetchDealerLoading}
          onClick={form.handleSubmit(onSubmit)}
        >
          {t(TextCodes.BuyersCertificate.Block.CreateNewCertificate)}
        </Button>
      </Box>
    </Box>
  );
};
